import { useMemo, useState } from "react";

import clsx from "clsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useBlockNavigation } from "@features/breakdown-v2/breakdowns/hooks";
import { useLabelWizard } from "@features/breakdown-v2/label-rule-wizard/hooks";
import {
  GET_LABEL_RULES_BY_GROUP_V3,
  GET_LABEL_RULE_GROUPS_V3,
  GET_LABEL_WIZARD_DATA_V3,
  UPDATE_LABEL_RULE_GROUP_V3,
} from "@gql/label-rules-v3";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type GetLabelRulesByGroupV3_me_labelRulesByGroupV3,
  type UpdateLabelRuleGroupV3,
  type UpdateLabelRuleGroupV3Variables,
} from "@north-beam/nb-client-side";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { notifyLabelsCache } from "@shared/notify-labels-cache";
import Spinner from "@shared/spinner";

import { ExitEditorComfirmationModal } from "../exit-editor-comfirmation-modal";

import {
  AdvancedLabelRuleWizard,
  type AdvancedSegmentToEdit,
} from "./advanced-label-rule-wizard";
import { SegmentModal } from "./segment-modal";
import { type AdvancedBreakdownDescriptor } from "./types";
import {
  advancedBreakdownToLabelRules,
  labelRulesToAdvancedBreakdown,
  validateAdvancedBreakdown,
} from "./utils";

interface Props {
  labelRules: GetLabelRulesByGroupV3_me_labelRulesByGroupV3[];
  deleting: boolean;
  duplicating: boolean;
  onDelete: () => void;
  onDuplicate: () => void;
}

export const AdvancedEditor = ({
  labelRules,
  deleting,
  duplicating,
  onDelete,
  onDuplicate,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToasts();
  const { labelNames: allLabelNames } = useLabelWizard();

  const [breakdown, setBreakdown] = useState<AdvancedBreakdownDescriptor>(() =>
    labelRulesToAdvancedBreakdown(labelRules),
  );
  const [segmentToEdit, setSegmentToEdit] = useState<AdvancedSegmentToEdit>({
    isNew: true,
    segment: null,
  });
  const [isRaw, setIsRaw] = useState(false);
  const [isBreakdownModified, setIsBreakdownModified] = useState(false);
  const [showExitEditorConfirmModal, setShowExitEditorConfirmModal] =
    useState(false);
  const [blockedNavigationUrl, setBlockedNavigationUrl] = useState<
    string | null
  >(null);

  const [updateLabelRuleGroup, { loading: saving }] = useNorthbeamMutation<
    UpdateLabelRuleGroupV3,
    UpdateLabelRuleGroupV3Variables
  >(UPDATE_LABEL_RULE_GROUP_V3, {
    useCpuIntensive: true,
    refetchQueries: [
      { query: GET_LABEL_RULE_GROUPS_V3 },
      {
        query: GET_LABEL_RULES_BY_GROUP_V3,
        variables: { id: labelRules[0].groupId },
      },
      { query: GET_LABEL_WIZARD_DATA_V3 },
    ],
    awaitRefetchQueries: true,
  });

  const labelNames = useMemo(
    () => allLabelNames.filter((e) => e !== labelRules[0].name),
    [allLabelNames, labelRules],
  );

  useBlockNavigation({
    blockNavigation: isBreakdownModified,
    onBlock: (blockedUrl) => {
      setBlockedNavigationUrl(blockedUrl);
      setShowExitEditorConfirmModal(true);
    },
  });

  const updateBreakdown = (breakdown: AdvancedBreakdownDescriptor) => {
    setBreakdown(breakdown);
    setIsBreakdownModified(true);
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <SecondaryButton
          className="flex items-center gap-2 font-bold"
          onClick={() => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onClick",
              additionalActionInfo: "Exit breakdown advanced edit page",
            });

            const desinationPath = user.pathFromRoot(
              `/breakdowns${location.search}`,
            );

            if (isBreakdownModified) {
              setShowExitEditorConfirmModal(true);
              setBlockedNavigationUrl(desinationPath);
            } else {
              navigate(desinationPath);
            }
          }}
        >
          <i className="fa-regular fa-arrow-left text-cosmic" /> BACK
        </SecondaryButton>
        <div className="flex items-center gap-2">
          <SecondaryButton
            className={clsx("flex items-center gap-2 font-bold", {
              "text-mars": !(deleting || saving || duplicating),
            })}
            disabled={deleting || saving || duplicating}
            onClick={() => onDelete()}
          >
            {deleting ? (
              <Spinner colorClassname="text-white-400" />
            ) : (
              <i className="fa-regular fa-trash text-mars" />
            )}
            DELETE
          </SecondaryButton>
          <SecondaryButton
            className="flex items-center gap-2 font-bold"
            disabled={deleting || saving || duplicating}
            onClick={onDuplicate}
          >
            {duplicating ? (
              <Spinner colorClassname="text-white-400" />
            ) : (
              <i className="fa-regular fa-copy text-cosmic" />
            )}
            DUPLICATE
          </SecondaryButton>
          <PrimaryButton
            className="flex items-center gap-2 font-bold"
            disabled={deleting || saving || duplicating || !isBreakdownModified}
            onClick={async () => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onClick",
                additionalActionInfo: "Update breakdown with advanced editor",
              });

              const validatedBreakdown = validateAdvancedBreakdown(
                breakdown,
                labelNames,
              );

              if (validatedBreakdown.error) {
                updateBreakdown(validatedBreakdown);
              } else {
                const result = advancedBreakdownToLabelRules(breakdown);

                await updateLabelRuleGroup({
                  variables: {
                    id: breakdown.groupId,
                    campaignLabelRule: result.campaign,
                    adsetLabelRule: result.adset,
                    adLabelRule: result.ad,
                  },
                });

                addToast(`Breakdown "${breakdown.name}" updated`, {
                  appearance: "success",
                });
                notifyLabelsCache();

                navigate(user.pathFromRoot(`/breakdowns${location.search}`));
              }
            }}
          >
            {saving ? (
              <Spinner colorClassname="text-white-400" />
            ) : (
              <i className="fa-regular fa-circle-plus" />
            )}
            SAVE
          </PrimaryButton>
        </div>
      </div>
      <div
        className={clsx(
          "bg-white border border-gray rounded-lg pt-6 pb-4 px-4",
          {
            "min-h-[calc(100vh-56px-50px-48px)]": isRaw,
            "h-[calc(100vh-56px-50px-48px)]": !isRaw,
          },
        )}
      >
        <AdvancedLabelRuleWizard
          advancedBreakdown={breakdown}
          isRaw={isRaw}
          onChange={(value) => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onChange",
              additionalActionInfo: "Update breakdown with advanced editor",
              data: {
                newBreakdown: value,
                pageContext: { breakdown },
              },
            });

            updateBreakdown(value);
          }}
          onEditSegment={(value) => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onChange",
              additionalActionInfo: "Open edit advanced editor segment modal",
              data: {
                newSegment: value,
              },
            });

            setSegmentToEdit(value);
          }}
          onChangeRaw={(value) => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onChange",
              additionalActionInfo:
                "Update breakdown with advanced editor in raw mode",
            });

            setIsRaw(value);
          }}
        />
      </div>
      <SegmentModal
        isUpdate={!segmentToEdit.isNew}
        breakdown={breakdown}
        segment={segmentToEdit.segment}
        onClose={() => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onChange",
            additionalActionInfo: "Close edit advanced editor segment modal",
          });

          setSegmentToEdit({ isNew: true, segment: null });
        }}
        onUpdate={(segment) => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onChange",
            additionalActionInfo: "Update advanced editor segment",
          });

          if (breakdown.segments.map((e) => e.id).includes(segment.id)) {
            updateBreakdown({
              ...breakdown,
              segments: breakdown.segments.map((e) =>
                e.id === segment.id ? segment : e,
              ),
            });
          } else {
            updateBreakdown({
              ...breakdown,
              segments: [...breakdown.segments, segment],
            });
          }

          setSegmentToEdit({ isNew: true, segment: null });
        }}
      />
      <ExitEditorComfirmationModal
        isOpen={showExitEditorConfirmModal}
        onClose={() => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onClick",
            additionalActionInfo:
              "Close exit advanced editor page confirmation modal",
          });

          setShowExitEditorConfirmModal(false);
          setBlockedNavigationUrl(null);
        }}
        onConfirm={() => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onClick",
            additionalActionInfo: "Confirm exit advanced editor page",
          });

          if (blockedNavigationUrl) {
            setShowExitEditorConfirmModal(false);
            navigate(blockedNavigationUrl);
            setBlockedNavigationUrl(null);
          }
        }}
      />
    </div>
  );
};
